<div fxLayout="column" fxLayoutAlign="start stretch" class="dashboard-container">

  <!-- Dashboard Header -->
  <div fxLayout="row" fxLayoutAlign="space-between center" class="dashboard-header">
    <div>
      <h1 class="dashboard-title">
        <mat-icon inline="true">dashboard</mat-icon>
        WBHS Alumni Dashboard
      </h1>
    </div>
    <div>
      <button mat-raised-button color="primary" (click)="refreshDashboard()" [disabled]="loading">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </div>

  <!-- Loading Spinner -->
  <div *ngIf="loading" fxLayout="row" fxLayoutAlign="center center" class="loading-container">
    <mat-spinner></mat-spinner>
  </div>

  <!-- Records with Issues Banner -->
  <mat-card *ngIf="recordIssues.length > 0" class="issues-banner" [ngClass]="{'expanded': showAllIssues}">
    <mat-card-content>
      <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="12px">
        <mat-icon color="warn">warning</mat-icon>
        <div fxFlex>
          <strong>Issues have been detected with the following records.</strong>
          Please check and correct the records before proceeding.
        </div>
        <button mat-icon-button (click)="toggleShowAllIssues()">
          <mat-icon>{{showAllIssues ? 'expand_less' : 'expand_more'}}</mat-icon>
        </button>
      </div>

      <div class="issues-list" fxLayout="row wrap" fxLayoutGap="16px" fxLayoutGap.xs="8px">
        <div *ngFor="let issue of displayedIssues"
             class="issue-item"
             fxLayout="row"
             fxLayoutAlign="start center"
             fxLayoutGap="8px"
             (click)="onRecordClick(issue)">
          <mat-icon [color]="getIssueColor(issue.type)" class="issue-icon">
            {{ getIssueIcon(issue.type) }}
          </mat-icon>
          <span class="issue-name">{{ issue.name }}</span>
          <span class="issue-description">({{ issue.issue }})</span>
        </div>
      </div>

      <div *ngIf="recordIssues.length > 3 && !showAllIssues" class="show-all-button">
        <button mat-button color="primary" (click)="toggleShowAllIssues()">
          Show All ({{ recordIssues.length }})
        </button>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Main Dashboard Grid -->
  <div fxLayout="row wrap" fxLayoutGap="20px" class="dashboard-grid" *ngIf="!loading">

    <!-- Record Types Widget -->
    <mat-card class="widget-card" fxFlex="0 1 calc(33.33% - 20px)" fxFlex.xs="100" fxFlex.sm="0 1 calc(50% - 20px)">
      <mat-card-header class="widget-header members-header">
        <mat-card-title>MEMBERS</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="donut-chart-container" fxLayout="column" fxLayoutAlign="center center">
          <div class="chart-placeholder donut-placeholder">
            <div class="donut-center">
              <div class="total-number">{{ formatNumber(totalRecords) }}</div>
              <div class="total-label">Total</div>
            </div>
            <div class="donut-segments" [style.background]="donutGradient">
            </div>
          </div>
          <div class="chart-legend" fxLayout="column" fxLayoutGap="8px">
            <div *ngFor="let type of recordTypes"
                 fxLayout="row"
                 fxLayoutAlign="start center"
                 fxLayoutGap="8px">
              <div class="legend-color" [style.background-color]="type.color"></div>
              <span class="legend-label">{{ type.label }}</span>
              <span class="legend-value">{{ formatNumber(type.count) }}</span>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Finance Widget -->
    <mat-card class="widget-card" fxFlex="0 1 calc(33.33% - 20px)" fxFlex.xs="100" fxFlex.sm="0 1 calc(50% - 20px)">
      <mat-card-header class="widget-header finance-header">
        <mat-card-title>FINANCE</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="finance-content">
          <div class="outstanding-fees">
            <h3>Outstanding Fees: {{ formatCurrency(outstandingFees) }}</h3>
          </div>
          <div class="chart-placeholder line-chart-placeholder" *ngIf="financeData.length > 0">
            <div class="line-chart-mock">
              <svg width="100%" height="120" viewBox="0 0 300 120">
                <!-- Y-axis labels -->
                <text *ngFor="let label of financeYLabels; let i = index"
                      x="5" [attr.y]="20 + i * 30" font-size="10" fill="#666">{{ label }}</text>

                <!-- Grid lines -->
                <line x1="25" y1="20" x2="280" y2="20" stroke="#f0f0f0" stroke-width="1"/>
                <line x1="25" y1="50" x2="280" y2="50" stroke="#f0f0f0" stroke-width="1"/>
                <line x1="25" y1="80" x2="280" y2="80" stroke="#f0f0f0" stroke-width="1"/>
                <line x1="25" y1="110" x2="280" y2="110" stroke="#f0f0f0" stroke-width="1"/>

                <!-- Finance data line -->
                <path [attr.d]="financeChartPath"
                      stroke="#2196F3"
                      stroke-width="2"
                      fill="none"/>

                <!-- Data points -->
                <circle *ngFor="let point of financeChartPoints"
                        [attr.cx]="point.cx" [attr.cy]="point.cy" r="3" fill="#2196F3"/>
              </svg>
              <div class="chart-labels finance-labels" fxLayout="row" fxLayoutAlign="space-around center">
                <span *ngFor="let data of financeData" class="month-label">{{ data.month }}</span>
              </div>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Recent Activity Widget -->
    <mat-card class="widget-card" fxFlex="0 1 calc(33.33% - 20px)" fxFlex.xs="100" fxFlex.sm="0 1 calc(50% - 20px)">
      <mat-card-header class="widget-header events-header">
        <mat-card-title>RECENT ACTIVITY</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="notifications-list" fxLayout="column" fxLayoutGap="12px">
          <div *ngFor="let activity of recentActivity.slice(0, 4)"
               class="notification-item"
               (click)="onActivityClick(activity)">
            <div class="notification-header" fxLayout="row" fxLayoutAlign="space-between start">
              <span class="notification-title">{{ activity.title }}</span>
              <span class="notification-time">{{ getTimeAgo(activity.timestamp) }}</span>
            </div>
            <div class="notification-message">{{ activity.message }}</div>
          </div>
          <div *ngIf="recentActivity.length === 0" class="no-notifications">
            <mat-icon>notifications_none</mat-icon>
            <span>No recent activity</span>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Paid Members Over Time -->
    <mat-card class="widget-card chart-widget" fxFlex="0 1 calc(50% - 20px)" fxFlex.xs="100">
      <mat-card-header class="widget-header">
        <mat-card-title>
          <mat-icon>people</mat-icon>
          Paid-up Members Over Time (12 months)
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="chart-placeholder line-chart-full" *ngIf="paidMembersData.length > 0">
          <svg width="100%" height="150" viewBox="0 0 520 150">
            <!-- Y-axis labels -->
            <text *ngFor="let label of paidMembersYLabels; let i = index"
                  x="15" [attr.y]="25 + i * 25" font-size="11" fill="#666">{{ label }}</text>

            <!-- Grid lines -->
            <line x1="45" y1="20" x2="510" y2="20" stroke="#f0f0f0" stroke-width="1"/>
            <line x1="45" y1="45" x2="510" y2="45" stroke="#f0f0f0" stroke-width="1"/>
            <line x1="45" y1="70" x2="510" y2="70" stroke="#f0f0f0" stroke-width="1"/>
            <line x1="45" y1="95" x2="510" y2="95" stroke="#f0f0f0" stroke-width="1"/>
            <line x1="45" y1="120" x2="510" y2="120" stroke="#f0f0f0" stroke-width="1"/>

            <!-- Members line -->
            <path [attr.d]="paidMembersChartPath"
                  stroke="#4CAF50"
                  stroke-width="2"
                  fill="none"/>
          </svg>
          <div class="chart-labels members-labels" fxLayout="row" fxLayoutAlign="space-between center">
            <span>{{ paidMembersStartLabel }}</span>
            <span>{{ paidMembersCurrentLabel }}</span>
            <span>{{ paidMembersEndLabel }}</span>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Email Coverage -->
    <mat-card class="widget-card chart-widget" fxFlex="0 1 calc(50% - 20px)" fxFlex.xs="100">
      <mat-card-header class="widget-header">
        <mat-card-title>
          <mat-icon>email</mat-icon>
          Email Coverage
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="email-coverage-content" fxLayout="column" fxLayoutAlign="center center">
          <div class="email-coverage-stat">
            <div class="coverage-number">{{ emailCoverage.percentage }}%</div>
            <div class="coverage-label">of records have an email address</div>
          </div>
          <div class="coverage-detail" fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="20px">
            <div class="coverage-item">
              <div class="coverage-item-number">{{ formatNumber(emailCoverage.withEmail) }}</div>
              <div class="coverage-item-label">With Email</div>
            </div>
            <div class="coverage-item">
              <div class="coverage-item-number">{{ formatNumber(emailCoverage.total) }}</div>
              <div class="coverage-item-label">Total Records</div>
            </div>
          </div>
          <div class="coverage-bar-container">
            <div class="custom-progress-bar coverage-bar">
              <div class="progress-fill" [style.width.%]="emailCoverage.percentage"></div>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Fundraising Projects -->
    <mat-card class="widget-card" fxFlex="100">
      <mat-card-header class="widget-header">
        <mat-card-title>
          <mat-icon>attach_money</mat-icon>
          Fundraising Projects - Donations to Date
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div fxLayout="row wrap" fxLayoutGap="20px" fxLayoutGap.xs="10px">
          <div *ngFor="let project of fundraisingProjects"
               class="fundraising-project"
               fxFlex="0 1 calc(25% - 20px)"
               fxFlex.xs="0 1 calc(50% - 10px)"
               fxFlex.sm="0 1 calc(50% - 20px)">
            <div class="project-name">{{ project.name }}</div>
            <div class="project-progress">
              <div class="custom-progress-bar">
                <div class="progress-fill" [style.width.%]="project.percentage"></div>
              </div>
              <div class="progress-text" fxLayout="row" fxLayoutAlign="space-between center">
                <span>{{ formatCurrency(project.raised) }}</span>
                <span>{{ project.percentage }}%</span>
              </div>
              <div class="target-text">Target: {{ formatCurrency(project.target) }}</div>
            </div>
          </div>
          <div *ngIf="fundraisingProjects.length === 0" class="no-projects">
            No active fundraising projects
          </div>
        </div>
      </mat-card-content>
    </mat-card>

  </div>
</div>
